import { useProcess } from '@fasstech/oip-starter-utils/app';
import { useForm } from 'react-hook-form';
import { useEffect, useRef, useState } from 'react';
import { isNilOrEmpty, isNotNilOrEmpty } from '@@serverHelpers/index.mjs';
import useGetSimulationCompanySearchCompanyQuery from './useGetSimulationCompanySearchCompanyQuery.js';
import { isSiretInvalid } from '@@viewHelpers/validations/siret.js';
import { applySpec, cond, equals, find, isNil, map, path, pathOr, prop, propEq, sortBy, trim } from 'ramda';
import useSaveSimulationCompanyMutation from './useSaveSimulationCompanyMutation.js';
import useGetSimulationCompanySearchCompanyCcnsQuery from './useGetSimulationCompanySearchCompanyCcnsQuery.js';
import useGetSimulationCompanySearchCompanyOtherCcnsQuery from './useGetSimulationCompanySearchCompanyOtherCcnsQuery.js';
import useGetSimulationCompanyCcnOfferQuery from './useGetSimulationCompanyCcnOfferQuery.js';
import useGetSimulationCompanyFlexiproductQuery from '@@views/simulationCompany/client/hooks/useGetSimulationCompanyFlexiproductQuery.js';

const useSimulationCompanyForm = ({ company, selectedCompany }) => {
  const { processHandlers, context } = useProcess();
  const [loading, setLoading] = useState(false);
  const [loadingCcns, setLoadingCcns] = useState(false);
  const [loadingOtherCcns, setLoadingOtherCcns] = useState(false);
  const [loadingCcnOffer, setLoadingCcnOffer] = useState(false);
  const [companies, setCompaniesState] = useState(null);
  const [noCCN, setNoCCN] = useState(false);
  const [ccns, setCcns] = useState(null);
  const [otherCcns, setOtherCcns] = useState(null);
  const [noCompaniesFound, setNoCompaniesFound] = useState(false);
  const [ccnOffer, setCcnOffer] = useState(null);
  const [ccnHasAgreement, setCcnHasAgreement] = useState(null);
  const [isUnauthorized, setIsUnauthorized] = useState(false);
  const [unauthorizedModalMessage, setUnauthorizedModalMessage] = useState('');

  const setCompanies = (companies) => {
    setNoCCN(false);
    setCompaniesState(companies);
  };

  const { onFormSubmit } = useSaveSimulationCompanyMutation();

  const form = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      company: {
        ccnRef: path(['company', 'ccnRef'], company),
        history: {
          name: path(['company', 'history', 'name'], company) ?? path(['company', 'name'], company),
          siret: path(['company', 'history', 'siret'], company) ?? path(['company', 'siret'], company),
          department: path(['company', 'history', 'department'], company) ?? path(['company', 'addresses', 0, 'department'], company)
        }
      }
    }
  });

  const { isValid } = form.formState;

  const search = isNotNilOrEmpty(form.watch('company.history.siret'))
    ? form.watch('company.history.siret')
    : form.watch('company.history.name');
  const department = form.watch('company.history.department');

  const { refetch } = useGetSimulationCompanySearchCompanyQuery({ skip: true });
  const { refetch: refetchCcns } =
    useGetSimulationCompanySearchCompanyCcnsQuery({ skip: true });
  const { refetch: refetchOtherCcns } =
    useGetSimulationCompanySearchCompanyOtherCcnsQuery({
      skip: true
    });
  const { refetch: refetchCcnOffer } = useGetSimulationCompanyCcnOfferQuery({
    skip: true
  });
  const { refetch: refetchFlexiproduct } =
    useGetSimulationCompanyFlexiproductQuery({ skip: true });

  // handle return from previous page
  useEffect(() => {
    // find history search
    if (
      isNotNilOrEmpty(path(['history', 'siret'], selectedCompany)) ||
      (isNotNilOrEmpty(path(['history', 'name'], selectedCompany)) &&
        isNotNilOrEmpty(path(['history', 'department'], selectedCompany)))
    ) {
      onSearch();
    }
    // find CCNS
    if (
      isNotNilOrEmpty(selectedCompany.history) &&
      isNotNilOrEmpty(selectedCompany.siret)
    ) {
      onSearchCcns({ company: selectedCompany });
    }
    // no CCN selected
    if (
      isNotNilOrEmpty(selectedCompany.history) &&
      isNilOrEmpty(selectedCompany.ccnRef)
    ) {
      setNoCCN(true);
      onSearchCcnOffer();
      onSearchFlexiproduct();
    }
  }, []);
  // if the ccn does not exist in the list of ccns, search for other ccns;
  const hasRunRef = useRef(false);
  useEffect(() => {
    // avoid running this useEffect multiple times
    if (hasRunRef.current) return;
    if (
      isNotNilOrEmpty(form.watch('company.ccnRef')) &&
      isNotNilOrEmpty(ccns) &&
      isNil(find(propEq('ref', form.watch('company.ccnRef')), ccns || []))
    ) {
      onSearchOtherCcns();
    }
  }, [ccns]);

  const onSearch = () => {
    setLoading(true);
    setCcns(null);
    setCcnOffer(null);
    refetch({
      search,
      department
    })
      .then(({ data }) => {
        const companies = pathOr(
          [],
          ['getSimulationCompanySearchCompany_simulation_company', 'companies'],
          data
        );
        setCompanies(companies);
        setNoCompaniesFound(companies.length === 0);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Erreur lors de la recherche d’entreprise:', err);
        setCompanies(null);
        setNoCompaniesFound(true);
        setLoading(false);
      });
  };

  const onSearchCcns = ({ company }) => {
    setLoadingCcns(true);
    refetchCcns({
      siret: company.siret
    })
      .then(({ data }) => {
        setCcns(
          pathOr(
            [],
            [
              'getSimulationCompanySearchCompanyCcns_simulation_company',
              'ccns'
            ],
            data
          )
        );
        setLoadingCcns(false);
      })
      .catch((err) => {
        console.error('Erreur lors de la recherche d’entreprise:', err);
        setCcns(null);
        setLoadingCcns(false);
      });
  };

  const onSearchOtherCcns = () => {
    setLoadingOtherCcns(true);
    refetchOtherCcns({})
      .then(({ data }) => {
        setOtherCcns(
          pathOr(
            [],
            [
              'getSimulationCompanySearchCompanyOtherCcns_simulation_company',
              'ccns'
            ],
            data
          )
        );
        setLoadingOtherCcns(false);
      })
      .catch((err) => {
        console.error('Erreur lors de la recherche d’entreprise:', err);
        setOtherCcns(null);
        setLoadingOtherCcns(false);
      });
  };

  const onSearchCcnOffer = (ccn = '', college = '') => {
    setLoadingCcnOffer(true);
    refetchCcnOffer({
      ccn,
      college
    })
      .then(({ data }) => {
        setCcnOffer(
          pathOr(
            false,
            ['getSimulationCompanyCcnOffer_simulation_company', 'ccnOffer'],
            data
          )
        );
        setLoadingCcnOffer(false);
      })
      .catch((err) => {
        console.error('Erreur lors de la recherche d’offre CCN:', err);
        setCcnOffer(null);
        setLoadingCcnOffer(false);
      });
  };

  const onSearchFlexiproduct = (idcc = '') => {
    refetchFlexiproduct({ idcc })
      .then(({ data }) => {
        if (path([
          'getSimulationCompanyFlexiproduct_simulation_company',
          'error'
        ],
        data
        )) {
          setIsUnauthorized(true);
          setUnauthorizedModalMessage(
            'Suite indisponibilité technique, les documents et/ou les informations produits ne sont pas disponibles. Veuillez réessayer ultérieurement. Nous mettons tout en œuvre pour résoudre le problème. En nous excusant de la gêne occasionnée.'
          );
        }
        setCcnHasAgreement(
          pathOr(
            null,
            [
              'getSimulationCompanyFlexiproduct_simulation_company',
              'ccnHasAgreement'
            ],
            data
          )
        );
      })
      .catch((err) => {
        console.error('Erreur lors de la recherche d’offre FlexiProduct:', err);
        setCcnHasAgreement(null);
      });
  };

  const onSubmit = (values) => {
    const ccnValues = cond([
      [
        () => equals(true, noCCN),
        () => ({ ccnId: '9999', ccnName: null, ccnRef: null })
      ],
      [
        () => equals(false, noCCN) && isNotNilOrEmpty(values.company.ccnRef),
        () => {
          const found =
            find(propEq('ref', values.company.ccnRef), ccns) ||
            find(propEq('ref', values.company.ccnRef), otherCcns);
          return applySpec({
            ccnId: prop('idcc'),
            ccnName: prop('name'),
            ccnRef: prop('ref')
          })(found);
        }
      ]
    ])();

    const formattedValues = {
      company: {
        name: selectedCompany.name,
        siret: selectedCompany.siret,
        siren: selectedCompany.siren,
        legalForm: selectedCompany.legalForm,
        addresses: [
          {
            department: selectedCompany.addresses[0].department,
            city: selectedCompany.addresses[0].city,
            zipCode: selectedCompany.addresses[0].zipCode,
            fields: selectedCompany.addresses[0].fields[0]
          }
        ],
        history: values.company.history || {},
        ccnHasAgreement: ccnHasAgreement,
        ...ccnValues
      }
    };

    onFormSubmit(formattedValues, (ok, err) => {
      if (ok && !err) {
        processHandlers.onNext({
          context: {
            projectId: context?.projectId
          }
        });
      }
    });
  };

  const options = map(
    (item) => ({
      label: item.name,
      value: item.ref
    }),
    ccns || []
  );

  let optionsOtherCcns = [];

  const sortAlphabeticlyByName = sortBy(prop('name'));
  if (!isNil(otherCcns)) {
    const trimmedCcns = map((ccn) => {
      return { 'name': trim(ccn.name), 'idcc': ccn.idcc, 'ref': ccn.ref };
    }, otherCcns);
    const sortedOtherCcns = sortAlphabeticlyByName(trimmedCcns);

    optionsOtherCcns = map(
      (item) => ({
        label: `${item.idcc} - ${item.name}`,
        value: item.ref
      }),
      sortedOtherCcns || []
    );
  }

  const isNameEmpty = isNilOrEmpty(form.watch('company.history.name'));
  const isDepartmentEmpty = isNilOrEmpty(
    form.watch('company.history.department')
  );
  const siret = form.watch('company.history.siret') || '';

  const disableSearch =
    isSiretInvalid(siret) && (isNameEmpty || isDepartmentEmpty);

  return {
    form,
    isValid,
    loading,
    onSearch,
    onSearchCcns,
    onSearchOtherCcns,
    companies,
    setCompanies,
    ccns,
    loadingCcns,
    otherCcns,
    loadingOtherCcns,
    disableSearch,
    onSubmit,
    noCompaniesFound,
    setCcns,
    noCCN,
    setNoCCN,
    options,
    optionsOtherCcns,
    onSearchCcnOffer,
    ccnOffer,
    loadingCcnOffer,
    setCcnOffer,
    setOtherCcns,
    onSearchFlexiproduct,
    isUnauthorized,
    unauthorizedModalMessage,
    setUnauthorizedModalMessage
  };
};

export default useSimulationCompanyForm;
